import React, { useState, useEffect, useContext, useMemo } from "react";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import Select from "react-select";
import { Link } from "react-router-dom";

// react-bootstrap components
import {
    Button,
    Card,
    Container,
    Row,
    Col,
    Spinner,
} from "react-bootstrap";
import CustomReactDatetime from "components/Custom/CustomReactDatetime";
import { getList, getMirthTasks } from "graphql/queries.js";
import { API, graphqlOperation } from "aws-amplify";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination";
import CustomTooltip from "../../components/Custom/CustomTooltip";
import { MultiValueWithCustomTooltip, customEllipsisMultiSelectStyles } from "components/Custom/CustomMultiSelect.js";
import { AuthContext } from "contexts/authContext";
import _ from "lodash";
import * as moment from "moment";
import { getTaskStatusLabel } from "utilities/uiMappingUtilities";

function MirthTasks(props) {
    // Internal styles
    const styles = {
        infoIcon: {
            fontSize: "13px",
            cursor: "pointer"
        },
        actionButtonsRow: {
            marginTop: "15px"
        }
    };

    // Hooks
    const notify = useContext(NotificationAlertContext);
    const currentUser = useContext(AuthContext);

    // SSO related information
    const stateProps = { ...props.location.state };
    const groupName = currentUser.groupName;
    const userClientId = currentUser.clientId;
    const userClientName = currentUser.clientName;
    const isInternalUser =
        groupName === "internal" || groupName === "internal_readonly";

    const clientName = !isInternalUser ? userClientName : stateProps?.clientName;
    const clientId = !isInternalUser ? userClientId : stateProps?.clientId;

    const statusOptions = [
        { value: 0, statuses: [0], label: "Created" },
        { value: 1, statuses: [1,2,3,4,5], label: "In Progress" },
        { value: 2, statuses: [6], label: "Completed" },
    ];

    const outcomeOptions = [
        { value: 0, label: "Success" },
        { value: 1, label: "Failure" },
    ];

    const taskType = [
        { value: "resendmessages", label: "Resend Messages" },
        { value: "deletemessages", label: "Delete Messages" },
        { value: "exportmessages", label: "Export Messages" }
    ]

    // States
    const [tableData, setTableData] = useState([]);
    const [showFirstLoader, setShowFirstLoader] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [showError, setShowError] = useState(false);
    const [startDate, setStartDate] = useState(stateProps?.startDate ? moment(stateProps.startDate) : null);
    const [endDate, setEndDate] = useState(stateProps?.endDate ? moment(stateProps.endDate) : null);
    const [selectedStatuses, setSelectedStatuses] = useState(stateProps?.selectedStatuses || []);
    const [selectedOutcomes, setSelectedOutcomes] = useState(stateProps?.selectedOutcomes || [{ value: 0, label: "Success" }]);
    const [selectedEnvironments, setSelectedEnvironments] = useState(stateProps?.selectedEnvironments || []);
    const [selectedTaskTypes, setSelectedTaskTypes] = useState(stateProps?.selectedTaskTypes || []);
    const [environmentOptions, setEnvironmentOptions] = useState([]);

    const headers = [
        { key: "cloud_task_id", label: "Cloud Task ID" },
        { key: "client_name", label: "Client" },
        { key: "environment_name", label: "Environment" },
        { key: "status", label: "Status" },
        { key: "outcome", label: "Outcome" },
        { key: "created_at", label: "Created At" },
        { key: "updated_at", label: "Updated At" },
    ];

    // Pagination Starts
    const columnMapping = {
        Environment: "environment_name",
        "Task Type": "task_type",
        Status: "status",
        Outcome: "outcome",
        "Created At": "created_at",
        "Updated At": "updated_at",
    };

    const [totalCount, setTotalCount] = useState(0);
    const numberOfRowsData = [25, 50, 100];

    const [currentLimit, setCurrentLimit] = useState(numberOfRowsData[1]);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [ascOrDescValue, setAscOrDescValue] = useState("ASC");
    const [sortValue, setSortValue] = useState("Client");
    const [showDownloadGridResults, setShowDownloadGridResults] = useState(true);

    const setStateForPagnination = async (key, value) => {
        if (key === "currentLimit") {
            setCurrentLimit(value);
        } else if (key === "offset") {
            setCurrentOffset(value);
        } else if (key === "ascOrDesc") {
            setAscOrDescValue(value);
        } else {
            setSortValue(value);
        }
    };
    // Pagination Ends

    const [selectedClient, setSelectedClient] = useState({
        value: clientId ? clientId : "",
        label: clientName ? clientName : "Select Client",
    });
    const [clientOptions, setClientOptions] = useState([]);
    const [clientData, setClientData] = useState([]);

    // Fetch options for clients
    const fetchClientList = async () => {
        try {
            const response = await API.graphql(
                graphqlOperation(getList, {
                    type: "CLIENT_ENVIRONMENT_HASH",
                    auth_client_id: currentUser.clientId,
                    is_active: true,
                })
            );
            if (response.data?.getList?.success) {
                const clientNameOptions = response.data.getList
                    ? response.data.getList.body
                        .map((client) => {
                            const option = {
                                value: client.id,
                                label: client.name,
                            };
                            return option;
                        })
                        .sort((a, b) => a.label.localeCompare(b.label))
                    : [];
                let clientData = _.get(response, 'data.getList.body', []);
                setClientData(clientData);
                if(selectedEnvironments.length > 0 || clientId){
                    const environment_client_hashmap = clientData.find(x => {
                        return x.id === selectedClient.value;
                    });
                    setEnvironmentOptions(environment_client_hashmap.hash);
                }
                setClientOptions(clientNameOptions);
            } else {
                console.error(response.data?.getList?.message);
                notify("danger", `Could not fetch clients list.`);
            }
            return response.data?.getList?.success;
        } catch (error) {
            console.error(error);
            notify("danger", `Could not fetch clients list.`);
            return false;
        }
    };

    const fetchMirthTasks = async (filterSearchObj, isCSVDownload = false) => {
        try {
            setShowLoader(true);
            let status = selectedStatuses?.length ? selectedStatuses.flatMap(option => option.statuses) : undefined;
            const payload = {
                ...filterSearchObj,
                client_id: selectedClient?.value,
                client_environment_ids: selectedEnvironments?.map(env => env.value).length ? selectedEnvironments.map(env => env.value) : undefined,
                status: status,
                outcome: selectedOutcomes?.map(outcome => outcome.value).length ? selectedOutcomes.map(outcome => outcome.value) : undefined,
                task_type: selectedTaskTypes?.map(taskType => taskType.value).length ? selectedTaskTypes.map(taskType => taskType.value) : undefined,
                start_date: startDate ? startDate : undefined,
                end_date: endDate ? endDate : undefined,
            }
            const response = await API.graphql(
                graphqlOperation(getMirthTasks, payload)
            );
            const body = response.data.getMirthTasks.body;
            const tasks = body.mirthTasks;
            const totalCount = body.totalCount;

            setShowDownloadGridResults(true);
            if (!isCSVDownload) {
                setTableData(tasks);
                setTotalCount(totalCount);
            } else {
                const filter = {
                    "Start Date": startDate || "Not specified",
                    "End Date": endDate || "Not specified",
                };
                return { messageDataForCSV: tasks, filter };
            }

            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setShowLoader(false);
        }
    };

    useEffect(() => {
        async function fetchData() {
            setShowFirstLoader(true);
            const filterObj = {
                offSet: 0,
                limit: 50,
            };
            const promiseAllResults = await Promise.all([
                fetchClientList(),
            ]);
            setShowFirstLoader(false);
            const isSuccess = promiseAllResults.every((value) => value);
            if (!isSuccess) {
                notify("danger");
                setShowError(true);
            } 
            // If state values exist, fetch Mirth tasks
            if (stateProps && (stateProps.selectedEnvironments?.length > 0 || 
                stateProps.selectedStatuses?.length > 0 || 
                stateProps.selectedOutcomes?.length > 0 || 
                stateProps.startDate || 
                stateProps.endDate)) {
                const filterObj = {
                    offSet: 0,
                    limit: currentLimit,
                    sortFilter: columnMapping[sortValue],
                    sortType: ascOrDescValue,
                };
                await fetchMirthTasks(filterObj);
            }
        }
        fetchData();
    }, []);

    // Helper function to format date to YYYY_MM_DD format
    const formatDateRollup = (dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}_${month}_${day}`;
        } catch (error) {
            console.error("Error formatting date:", error);
            return dateString; // Return original if formatting fails
        }
    };

    if (showFirstLoader || showLoader) return <Spinner animation="grow" />;

    if (showError)
        return `Could not load the page at this moment. Please try again later.`;

    return (
        <>
            <Container fluid>
                <Row>
                    <Col>
                        <h2>Mirth Tasks</h2>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Card>
                            <Card.Header>
                                <Card.Title>Tasks Search</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Row className="align-items-end">
                                    <Col md="4" sm="12">
                                        <label>Client (required)</label>
                                        <Select
                                            className="react-select primary"
                                            classNamePrefix="react-select"
                                            id="clientFilter"
                                            name="clientFilter"
                                            closeMenuOnSelect={true}
                                            isMulti={false}
                                            value={selectedClient}
                                            onChange={(client) => {
                                                setSelectedClient(client);
                                                setSelectedEnvironments([]);
                                                const environment_client_hashmap = clientData.find(x => {
                                                    return x.id === client.value;
                                                });
                                                setEnvironmentOptions(environment_client_hashmap.hash);
                                            }}
                                            options={clientOptions}
                                            isDisabled={!isInternalUser}
                                            placeholder="Select Client"
                                        />
                                    </Col>
                                    <Col md="4" sm="12">
                                        <label>Client Environment</label>
                                        <Select
                                            className="react-select primary"
                                            classNamePrefix="react-select"
                                            id="environmentFilter"
                                            name="environmentFilter"
                                            closeMenuOnSelect={false}
                                            isMulti
                                            styles={customEllipsisMultiSelectStyles}
                                            components={{ MultiValue: MultiValueWithCustomTooltip }}
                                            value={selectedEnvironments}
                                            onChange={(environments) => {
                                                setSelectedEnvironments(environments || []);
                                            }}
                                            options={environmentOptions}
                                            isClearable={false}
                                            placeholder="Filter by Environment"
                                        />
                                    </Col>
                                    <Col md="4" sm="12">
                                        <label>Status</label>
                                        <Select
                                            className="react-select primary"
                                            classNamePrefix="react-select"
                                            id="statusFilter"
                                            name="statusFilter"
                                            closeMenuOnSelect={false}
                                            isMulti
                                            styles={customEllipsisMultiSelectStyles}
                                            components={{ MultiValue: MultiValueWithCustomTooltip }}
                                            value={selectedStatuses}
                                            onChange={setSelectedStatuses}
                                            options={statusOptions}
                                            isClearable={false}
                                            placeholder="Filter by Status"
                                        />
                                    </Col>
                                </Row>

                                <Row className="align-items-end">
                                    <Col md="3" sm="12">
                                        <label>Start Date <CustomTooltip
                                            id="start-date-tooltip"
                                            value='Based on "Created At"'
                                            placement="left"
                                        ><i className="fa fa-info-circle"
                                            style={styles.infoIcon}></i></CustomTooltip></label>
                                        <CustomReactDatetime
                                            inputProps={{
                                                className: "form-control",
                                                placeholder: "Task Start Date",
                                                value: startDate
                                                    ? moment(startDate).format(
                                                        "MM/DD/YYYY h:mm A"
                                                    )
                                                    : "",
                                            }}
                                            timeFormat={true}
                                            value={startDate}
                                            onChange={setStartDate}
                                        ></CustomReactDatetime>
                                    </Col>
                                    <Col md="3" sm="12">
                                        <label>End Date <CustomTooltip
                                            id="end-date-tooltip"
                                            value='Based on "Created At"'
                                            placement="left"
                                        ><i className="fa fa-info-circle"
                                            style={styles.infoIcon}></i></CustomTooltip></label>
                                        <CustomReactDatetime
                                            inputProps={{
                                                className: "form-control",
                                                placeholder: "Task End Date",
                                                value: endDate
                                                    ? moment(endDate).format(
                                                        "MM/DD/YYYY h:mm A"
                                                    )
                                                    : "",
                                            }}
                                            timeFormat={true}
                                            value={endDate}
                                            onChange={setEndDate}
                                        ></CustomReactDatetime>
                                    </Col>
                                    <Col md="3" sm="12">
                                        <label>Outcome</label>
                                        <Select
                                            className="react-select primary"
                                            classNamePrefix="react-select"
                                            id="outcomeFilter"
                                            name="outcomeFilter"
                                            closeMenuOnSelect={false}
                                            isMulti
                                            styles={customEllipsisMultiSelectStyles}
                                            components={{ MultiValue: MultiValueWithCustomTooltip }}
                                            value={selectedOutcomes}
                                            onChange={setSelectedOutcomes}
                                            options={outcomeOptions}
                                            isClearable={false}
                                            placeholder="Filter by Outcome"
                                        />
                                    </Col>
                                    <Col md="3" sm="12">
                                        <label>Task Type</label>
                                        <Select
                                            className="react-select primary"
                                            classNamePrefix="react-select"
                                            id="taskTypeFilter"
                                            name="taskTypeFilter"
                                            closeMenuOnSelect={false}
                                            isMulti
                                            styles={customEllipsisMultiSelectStyles}
                                            components={{ MultiValue: MultiValueWithCustomTooltip }}
                                            value={selectedTaskTypes}
                                            onChange={setSelectedTaskTypes}
                                            options={taskType}
                                            isClearable={false}
                                            placeholder="Filter by Task Type"
                                        />
                                    </Col>
                                </Row>

                                <Row className="align-items-end" key="action-buttons-row" style={styles.actionButtonsRow}>
                                    <Col>
                                        <Button
                                            variant="primary"
                                            onClick={async () => {
                                                // Collect validation errors
                                                const errors = [];

                                                // Validate start date is not after end date
                                                if (moment(startDate).isAfter(moment(endDate))) {
                                                    errors.push("Start date must be lesser than end date");
                                                }

                                                // Show all errors if any exist
                                                if (errors.length > 0) {
                                                    errors.forEach(error => notify("danger", error));
                                                    return;
                                                }

                                                setSortValue("Client");
                                                setAscOrDescValue("ASC");
                                                const filterObj = {
                                                    offSet: 0,
                                                    limit: 50,
                                                };
                                                const isSuccess = await fetchMirthTasks(filterObj);
                                                if (isSuccess) notify("success");
                                                else notify("danger");
                                            }}
                                        >
                                            Find Tasks
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            className="btn-outline ml-4"
                                            onClick={() => {
                                                if (isInternalUser) {
                                                    setSelectedClient(null);
                                                }
                                                setSelectedEnvironments([]);
                                                setSelectedStatuses([]);
                                                setSelectedOutcomes([{ value: 0, label: "Success" }]);
                                                setSelectedTaskTypes([]);
                                                setStartDate(null);
                                                setEndDate(null);
                                                setSortValue("Client");
                                                setAscOrDescValue("ASC");
                                                setShowDownloadGridResults(false);
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <ReactTable
                    data={tableData}
                    totalCount={totalCount}
                    functionCallBack={fetchMirthTasks}
                    setStateForPagnination={setStateForPagnination}
                    currentLimit={currentLimit}
                    currentOffset={currentOffset}
                    ascOrDescValue={ascOrDescValue}
                    sortValue={sortValue}
                    numberOfRowsData={numberOfRowsData}
                    columnMapping={columnMapping}
                    tableName="MirthTasks"
                    CSVHeaders={headers}
                    CSVFileName="MirthTasks_Export"
                    notify={notify}
                    showDownloadGridResults={showDownloadGridResults}
                    columns={[
                        {
                            Header: "Environment",
                            accessor: "environment_name",
                            filterable: false,
                            showTooltip: true,
                            minWidth: 160,
                        },
                        {
                            Header: "Task Type",
                            accessor: (row) => {
                                return taskType.find(status => status.value === row.task_type)?.label;
                            },
                            filterable: false,
                            showTooltip: true,
                            minWidth: 160,
                        },
                        {
                            Header: "Status",
                            accessor: (row) => {
                                return getTaskStatusLabel(row.status, row.task_type);
                            },
                            filterable: false,
                            showTooltip: true,
                            minWidth: 160,
                        },
                        {
                            Header: "Details",
                            Cell: (cell) => {
                                const row = cell.row.original;
                                if (row.status === 6) {
                                    return (
                                        <Link
                                            to={{
                                                pathname: `/admin/mirth-tasks/${row.cloud_task_id}/${row.task_type}`,
                                                state: {
                                                    clientId: String(row.client_id || ''),
                                                    clientName: String(row.client_name || ''),
                                                    selectedEnvironments: selectedEnvironments || [],
                                                    selectedStatuses: selectedStatuses || [],
                                                    selectedOutcomes: selectedOutcomes || [],
                                                    selectedTaskTypes: selectedTaskTypes || [],
                                                    startDate: startDate ? String(startDate) : '',
                                                    endDate: endDate ? String(endDate) : ''
                                                },
                                                search: `?clientEnvironmentId=${row.client_environment_id}&dateRollup=${formatDateRollup(row.created_at)}`,
                                            }}
                                        >
                                            View Task Details
                                        </Link>
                                    );
                                }
                            },
                            filterable: false,
                            sortable: false,
                        },
                        {
                            Header: "Outcome",
                            accessor: (row) => {
                                return outcomeOptions.find(outcome => outcome.value === row.outcome)?.label;
                            },
                            filterable: false,
                            showTooltip: true,
                            minWidth: 160,
                        },
                        {
                            Header: "Created At",
                            accessor: (row) => {
                                return new Date(row.created_at).toLocaleString();
                            },
                            filterable: false,
                            showTooltip: true,
                            minWidth: 160,
                        },
                        {
                            Header: "Updated At",
                            accessor: (row) => {
                                if (row.updated_at) {
                                    return new Date(row.updated_at).toLocaleString();
                                } else {
                                    return ''
                                }
                            },
                            filterable: false,
                            showTooltip: true,
                            minWidth: 160,
                        },
                        {
                            Header: "User Name",
                            accessor: (row) => {
                                return row.first_name + ' ' + row.last_name
                            },
                            filterable: false,
                            showTooltip: true,
                            minWidth: 160,
                            sortable: false,
                        },
                    ]}
                />
            </Container>
        </>
    );
}

export default MirthTasks;
